/**
 * RSI(2) Oversold Pullback service (Investment Ideas — research/screening
 * model, NOT an Algo Strategy).
 *
 * Talks only to `/api/v1/rsi2-pullback/*` — read-only for regular users
 * (list/detail/statistics); `runNow`/`backfill` are admin-only actions the
 * backend itself gates via `require_roles("ADMIN")` (a non-admin call
 * simply gets a 403 from the API, same as every other admin action in this
 * codebase — no client-side role check needed here).
 */

import api from "@/services/api";
import type {
  Rsi2PullbackChartResponse,
  Rsi2PullbackIdeaDetailResponse,
  Rsi2PullbackIdeaListResponse,
  Rsi2PullbackSortField,
  Rsi2PullbackStatisticsResponse,
  Rsi2PullbackTargetStatus,
  SortOrder,
} from "@/types";

export interface Rsi2PullbackListParams {
  status?: Rsi2PullbackTargetStatus;
  page?: number;
  page_size?: number;
  interval?: string;
  sort_by?: Rsi2PullbackSortField;
  sort_order?: SortOrder;
}

export interface Rsi2PullbackRunNowResponse {
  interval: string;
  processed: number;
  signals_created: number;
  references_resolved: number;
  outcomes_updated: number;
  failed: Record<string, string>;
}

export interface Rsi2PullbackBackfillResponse {
  interval: string;
  symbol?: string | null;
  signals_created?: number | null;
  bars_scanned?: number | null;
  processed_count?: number | null;
  failed?: Record<string, string> | null;
}

export const rsi2PullbackService = {
  async list(params: Rsi2PullbackListParams = {}): Promise<Rsi2PullbackIdeaListResponse> {
    const { data } = await api.get<Rsi2PullbackIdeaListResponse>("/rsi2-pullback/ideas", { params });
    return data;
  },

  async getDetail(
    symbol: string,
    signalDate: string,
    interval = "1day"
  ): Promise<Rsi2PullbackIdeaDetailResponse> {
    const { data } = await api.get<Rsi2PullbackIdeaDetailResponse>(
      `/rsi2-pullback/ideas/${encodeURIComponent(symbol)}/${encodeURIComponent(signalDate)}`,
      { params: { interval } }
    );
    return data;
  },

  async getChart(
    symbol: string,
    signalDate: string,
    interval = "1day"
  ): Promise<Rsi2PullbackChartResponse> {
    const { data } = await api.get<Rsi2PullbackChartResponse>(
      `/rsi2-pullback/ideas/${encodeURIComponent(symbol)}/${encodeURIComponent(signalDate)}/chart`,
      { params: { interval } }
    );
    return data;
  },

  async getStatistics(interval = "1day"): Promise<Rsi2PullbackStatisticsResponse> {
    const { data } = await api.get<Rsi2PullbackStatisticsResponse>("/rsi2-pullback/statistics", {
      params: { interval },
    });
    return data;
  },

  // ---- Admin-only actions ----

  async runNow(interval = "1day"): Promise<Rsi2PullbackRunNowResponse> {
    const { data } = await api.post<Rsi2PullbackRunNowResponse>("/rsi2-pullback/admin/run-now", null, {
      params: { interval },
    });
    return data;
  },

  async backfill(symbol?: string, interval = "1day"): Promise<Rsi2PullbackBackfillResponse> {
    const { data } = await api.post<Rsi2PullbackBackfillResponse>("/rsi2-pullback/admin/backfill", {
      symbol,
      interval,
    });
    return data;
  },
};

export default rsi2PullbackService;
